<script setup lang="ts">
/**
 * The harbour shown as it is: a screenshot of the running game (desktop,
 * 1440x900, a dev account dressed as "HMS Resolute") in a brass frame at its
 * own 16:10, and a kit row for each station under it with its one line.
 *
 * A picture, not a live HUD: the HUD lays itself out for the viewport, so a
 * copy squeezed into a frame on a phone overlapped itself. Retake the shot
 * (`public/art/scenes/harbour-game-*`) when the harbour changes.
 */
import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';
import KitRow from '@/ui/kit/KitRow.vue';

import { HARBOUR_STATION_ICON, HARBOUR_STATION_IDS } from '../landing-content';
import SectionShell from './SectionShell.vue';

const { t } = useI18n();
</script>

<template>
  <SectionShell id="harbour" icon="anchor" :title="t('landing.harbour.title')" :line="t('landing.harbour.line')" backdrop="/art/scenes/chandler.webp">
    <picture class="frame" data-slam="rise">
      <source type="image/webp" srcset="/art/scenes/harbour-game-960.webp 960w, /art/scenes/harbour-game-1600.webp 1600w" sizes="(min-width: 1140px) 1100px, 100vw" />
      <img
        class="shot"
        src="/art/scenes/harbour-game-1600.jpg"
        srcset="/art/scenes/harbour-game-960.jpg 960w, /art/scenes/harbour-game-1600.jpg 1600w"
        sizes="(min-width: 1140px) 1100px, 100vw"
        :alt="t('landing.harbour.frame')"
        width="1600"
        height="1000"
        loading="lazy"
        decoding="async"
      />
    </picture>

    <ul class="stations">
      <li v-for="(id, i) in HARBOUR_STATION_IDS" :key="id" data-slam="rise" :style="{ '--i': i }">
        <KitRow :title="t(`stations.${id}.label`)" :subtitle="t(`stations.${id}.hint`)">
          <template #art><GameIcon :name="HARBOUR_STATION_ICON[id]" :size="56" /></template>
        </KitRow>
      </li>
    </ul>
  </SectionShell>
</template>

<style scoped>
.frame {
  display: block;
  max-width: 1100px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 16px;
  background: #0d141c;
  box-shadow: 0 0 0 3px #0d141c, 0 0 0 6px #b98a3a, 0 20px 36px rgba(0, 0, 0, 0.55);
}

.shot {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
}

.stations {
  display: grid;
  gap: 10px;
  max-width: 1100px;
  margin: clamp(18px, 3vw, 32px) auto 0;
  padding: 0;
  list-style: none;
}

@media (min-width: 700px) {
  .stations {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1000px) {
  .stations {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
